import React, { useEffect, useId } from 'react';
import {
    Button,
    ButtonGroup,
    Col,
    FieldsetControl,
    RadioControl,
    Row,
    SegmentLoader,
} from '@si-armes/dsfr-react';
import { FormProvider, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import InfoChampsObligatoire from 'components/form/infoObligatoire/InfoChampsObligatoire';
import {
    FEATURE_COLLECTIONNEURS,
    isFeatureActive,
} from 'components/FeatureFlipping/featureFlipping.func';
import {
    CHOIX_STATUT_METIER_ASSO,
    KEY_DETENTEUR_COLLECTIONNEUR_PHYSIQUE,
    KEY_DETENTEUR_COLLECTIONNEUR_MORALE,
    KEY_DETENTEUR_ASSO_MORALE,
} from './StatutCompte.constante';
import { choisirStatutCompteMetierAsso } from '../../../../../../store/compte/creer/creerCompteReducer';
import SelectMetierAssociation from './SelectMetierAssociation';
import STATUS_METIER_ASSO from '../../../../../../utils/Statuts.MetierAsso.constant';

const StatutCompte = ({
    setStatutMetierAsso,
    statutCompteMetierAsso,
    choisirEtape,
    enregistrerStatutCompteMetierAsso,
    retourEtape,
    isLoading,
    errorMetiersEtAssociationsTypes,
    featureFlipping,
}) => {
    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: { ...statutCompteMetierAsso },
    });

    const { handleSubmit, register, formState, watch } = hookForm;

    // Récupérer la valeur actuelle du champ "statut" en temps réel
    const selectedStatut = watch('statut');

    // Utiliser useEffect pour réagir aux changements de statut
    // et déclencher les actions nécessaires
    useEffect(() => {
        if (selectedStatut) {
            setStatutMetierAsso(selectedStatut);
        }
    }, [selectedStatut]);

    const isCollectionneurPhysique = selectedStatut === KEY_DETENTEUR_COLLECTIONNEUR_PHYSIQUE;

    const isCollectionneurMorale = selectedStatut === KEY_DETENTEUR_COLLECTIONNEUR_MORALE;

    const isCollectionneur = isCollectionneurPhysique || isCollectionneurMorale;

    const onSubmit = (dataForm) => {
        let type;
        if (isCollectionneurMorale) {
            type = STATUS_METIER_ASSO.METIER_MORALE_COLLECTIONNEUR;
        } else if (isCollectionneur) {
            type = STATUS_METIER_ASSO.METIER_PHYSIQUE_COLLECTIONNEUR;
        } else {
            type = dataForm.typeMetierAssociation;
        }
        const dataToSend = {
            ...dataForm,
            type,
            typeMetierAssociation: type,
        };
        enregistrerStatutCompteMetierAsso(dataToSend);
        choisirEtape();
    };

    const isCollectionneursActive = isFeatureActive(featureFlipping, FEATURE_COLLECTIONNEURS);

    const filteredChoix = CHOIX_STATUT_METIER_ASSO.filter((choix) => {
        if (!isCollectionneursActive && choix.value === KEY_DETENTEUR_COLLECTIONNEUR_PHYSIQUE) {
            return false;
        }
        if (!isCollectionneursActive && choix.value === KEY_DETENTEUR_COLLECTIONNEUR_MORALE) {
            return false;
        }
        return true;
    });

    const colSize = filteredChoix.length <= 3 ? 4 : 2;

    return (
        <SegmentLoader isLoading={isLoading}>
            <Row className="margin-bottom-md">
                <Col col={12}>
                    <InfoChampsObligatoire />
                </Col>
            </Row>
            <Row>
                <Col col={12}>
                    <h2 className="fr-stepper__title">Informations sur le statut</h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <p className="margin-bottom-sm">
                        Veuillez sélectionner la situation vous concernant{' '}
                        <span className="text-red">*</span>
                    </p>
                </Col>
            </Row>
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FieldsetControl>
                        <Row gutters>
                            {filteredChoix.map((choix) => {
                                return (
                                    <Col key={`radio-col-${choix.value}`} col={12} lg={colSize}>
                                        <RadioControl
                                            key={`radio-${choix.value}`}
                                            id={useId()}
                                            {...register('statut', {
                                                required: 'Veuillez sélectionner un statut',
                                            })}
                                            label={choix.libelle}
                                            value={choix.value}
                                            disabled={
                                                selectedStatut && selectedStatut !== choix.value
                                            }
                                        />
                                    </Col>
                                );
                            })}
                        </Row>
                    </FieldsetControl>

                    {selectedStatut && !isCollectionneur && (
                        <div>
                            <SelectMetierAssociation
                                name="typeMetierAssociation"
                                statut={selectedStatut}
                                label={
                                    selectedStatut === KEY_DETENTEUR_ASSO_MORALE
                                        ? 'Sélectionner votre fédération de référence'
                                        : 'Sélectionner votre type de métier'
                                }
                                required
                            />
                        </div>
                    )}

                    <Row className="margin-top-lg">
                        <Col>
                            <ButtonGroup inline align="right">
                                <Button
                                    name="etapePrecedente"
                                    variant="secondary"
                                    type="button"
                                    isLoading={isLoading}
                                    onClick={retourEtape}
                                >
                                    Retour
                                </Button>
                                <Button
                                    name="etapeSuivante"
                                    variant="primary"
                                    type="submit"
                                    isLoading={isLoading}
                                    disabled={
                                        errorMetiersEtAssociationsTypes ||
                                        (!isCollectionneur && !formState.isValid)
                                    }
                                >
                                    Accéder à l'étape suivante
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
            </FormProvider>
        </SegmentLoader>
    );
};

const mapStateToProps = (state) => ({
    statutCompteMetierAsso: state.compte?.creer?.statutCompteMetierAsso,
    errorMetiersEtAssociationsTypes: state.referentiel.metiersEtAssociationsTypes?.error,
    featureFlipping: state.featureFlipping?.data,
});

const mapDispatchToProps = {
    enregistrerStatutCompteMetierAsso: choisirStatutCompteMetierAsso,
};

StatutCompte.propTypes = {
    setStatutMetierAsso: PropTypes.func.isRequired,
    retourEtape: PropTypes.func.isRequired,
    choisirEtape: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    enregistrerStatutCompteMetierAsso: PropTypes.func.isRequired,
    statutCompteMetierAsso: PropTypes.shape({
        statut: PropTypes.string,
        type: PropTypes.string,
    }),
    errorMetiersEtAssociationsTypes: PropTypes.bool,
    featureFlipping: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(StatutCompte);
